import type { MotionEditorState } from '../motionEditorStore';
import type { MotionEditorActor, SelectedSceneMotion } from '../types';

export interface KeyPoseMotionContext {
  appliedMotionEditJobID: string | null;
  bvhUrls: string[];
  sceneID: string;
  referenceName: string;
  motionID: string;
  originalBvhURL: string | null;
  timelineIndex: number;
}

interface ResolveKeyPoseMotionContextInput {
  scene: MotionEditorState['scene'];
  playback: MotionEditorState['playback'];
  keyPoseReferenceName?: string | null;
}

export function resolveKeyPoseMotionContext({
  scene,
  playback,
  keyPoseReferenceName,
}: ResolveKeyPoseMotionContextInput): KeyPoseMotionContext | null {
  if (!scene.data) {
    return null;
  }

  const referenceName = resolveActiveKeyPoseReferenceName(
    keyPoseReferenceName,
    playback.selectedReferenceName,
    playback.sceneActors,
  );
  if (!referenceName) {
    return null;
  }

  const selectedMotion = resolveSelectedKeyPoseMotionForReference(
    referenceName,
    playback.selectedReferenceName,
    playback.selectedSceneMotion,
    playback.sceneActors,
  );
  if (!selectedMotion) {
    return null;
  }

  return {
    appliedMotionEditJobID: selectedMotion.appliedMotionEditJobID,
    bvhUrls: selectedMotion.bvhUrls,
    sceneID: scene.data.sceneID,
    referenceName,
    motionID: selectedMotion.motionID,
    originalBvhURL: selectedMotion.originalBvhURL,
    timelineIndex: selectedMotion.timelineIndex,
  };
}

export function resolveActiveKeyPoseReferenceName(
  keyPoseReferenceName: string | null | undefined,
  selectedReferenceName: string | null,
  sceneActors: readonly { referenceName: string }[],
) {
  return (
    keyPoseReferenceName ??
    selectedReferenceName ??
    (sceneActors.length <= 1 ? (sceneActors[0]?.referenceName ?? null) : null)
  );
}

export function resolveSelectedKeyPoseMotionForReference(
  referenceName: string,
  selectedReferenceName: string | null,
  selectedSceneMotion: SelectedSceneMotion | null,
  sceneActors: readonly Pick<
    MotionEditorActor,
    'referenceName' | 'timelineMotions'
  >[],
) {
  if (
    selectedSceneMotion &&
    (selectedReferenceName === referenceName ||
      selectedSceneMotion.referenceName === referenceName)
  ) {
    return selectedSceneMotion;
  }

  return (
    sceneActors.find((actor) => actor.referenceName === referenceName)
      ?.timelineMotions[0] ?? null
  );
}
